import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  ArrowUp,
  ArrowDown,
  Equals,
  WarningCircle,
  Trash,
  Package,
} from '@phosphor-icons/react';
import { bizGet, bizPost, bizDelete } from '@/shared/api/bizClient';

const CATEGORIES = ['supplies', 'products', 'food', 'medication', 'equipment'] as const;
type Category = (typeof CATEGORIES)[number];

interface InventoryItem {
  id: string;
  name: string;
  description: string | null;
  sku: string | null;
  category: Category;
  unit: string;
  quantityOnHand: number;
  reorderThreshold: number | null;
  costMinor: number | null;
  isLowStock: boolean;
  createdAt: string;
}

interface ListResponse {
  items: InventoryItem[];
  total: number;
}

const CATEGORY_LABELS: Record<Category, string> = {
  supplies: 'Sarf Malzeme',
  products: 'Ürün',
  food: 'Mama/Yem',
  medication: 'İlaç',
  equipment: 'Ekipman',
};

const CATEGORY_COLORS: Record<Category, string> = {
  supplies: 'bg-blue-100 text-blue-700',
  products: 'bg-purple-100 text-purple-700',
  food: 'bg-green-100 text-green-700',
  medication: 'bg-red-100 text-red-700',
  equipment: 'bg-amber-100 text-amber-700',
};

function formatCurrency(minor: number | null): string {
  if (minor === null) return '—';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(minor / 100);
}

export function InventoryPage(): JSX.Element {
  const { t } = useTranslation('layout');
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [adjustItem, setAdjustItem] = useState<InventoryItem | null>(null);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [filterCategory, setFilterCategory] = useState<Category | ''>('');

  const { data, isLoading } = useQuery<ListResponse>({
    queryKey: ['biz-inventory', lowStockOnly, filterCategory],
    queryFn: () =>
      bizGet<ListResponse>(
        `/biz/inventory?lowStockOnly=${lowStockOnly}${filterCategory ? `&category=${filterCategory}` : ''}`,
      ),
  });

  const archiveMut = useMutation({
    mutationFn: (id: string) => bizDelete(`/biz/inventory/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['biz-inventory'] }),
  });

  const items = data?.items ?? [];
  const lowStockCount = items.filter((i) => i.isLowStock).length;

  return (
    <div className="flex flex-col gap-6 p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('nav.inventory')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data?.total ?? 0} kalem
            {lowStockCount > 0 && (
              <span className="ml-2 text-amber-600 font-medium">
                · {lowStockCount} düşük stok
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} weight="bold" />
          Yeni Kalem
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setLowStockOnly((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
            lowStockOnly
              ? 'bg-amber-100 text-amber-700 border-amber-300'
              : 'bg-background text-muted-foreground border-border hover:bg-muted'
          }`}
        >
          <WarningCircle size={14} />
          Düşük Stok
        </button>
        <button
          onClick={() => setFilterCategory('')}
          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
            filterCategory === ''
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background text-muted-foreground border-border hover:bg-muted'
          }`}
        >
          Tümü
        </button>
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilterCategory(filterCategory === cat ? '' : cat)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filterCategory === cat
                ? `${CATEGORY_COLORS[cat]} border-current`
                : 'bg-background text-muted-foreground border-border hover:bg-muted'
            }`}
          >
            {CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="grid gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Package size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground font-medium">Stok kalemi bulunamadı</p>
          <p className="text-sm text-muted-foreground mt-1">
            "Yeni Kalem" ile ilk stoğunuzu ekleyin.
          </p>
        </div>
      ) : (
        <div className="grid gap-2">
          {items.map((item) => (
            <div
              key={item.id}
              className={`flex items-center gap-4 p-4 bg-card border rounded-xl transition-colors hover:bg-muted/40 ${
                item.isLowStock ? 'border-amber-300 bg-amber-50/40' : 'border-border'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-foreground truncate">{item.name}</span>
                  {item.sku && (
                    <span className="text-xs text-muted-foreground font-mono">#{item.sku}</span>
                  )}
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[item.category]}`}
                  >
                    {CATEGORY_LABELS[item.category]}
                  </span>
                  {item.isLowStock && (
                    <span className="flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full font-medium">
                      <WarningCircle size={12} />
                      Düşük Stok
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                  <span>
                    <span className="font-semibold text-foreground">{item.quantityOnHand}</span>{' '}
                    {item.unit}
                  </span>
                  {item.reorderThreshold !== null && (
                    <span>Min: {item.reorderThreshold} {item.unit}</span>
                  )}
                  {item.costMinor !== null && (
                    <span>Maliyet: {formatCurrency(item.costMinor)}/{item.unit}</span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => setAdjustItem(item)}
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title="Stok düzenle"
                >
                  <Equals size={16} />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`"${item.name}" stok kalemini silmek istiyor musunuz?`)) {
                      archiveMut.mutate(item.id);
                    }
                  }}
                  className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  title="Sil"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Item Modal */}
      {showAdd && (
        <AddItemModal
          onClose={() => setShowAdd(false)}
          onSuccess={() => {
            setShowAdd(false);
            qc.invalidateQueries({ queryKey: ['biz-inventory'] });
          }}
        />
      )}

      {/* Adjust Modal */}
      {adjustItem && (
        <AdjustModal
          item={adjustItem}
          onClose={() => setAdjustItem(null)}
          onSuccess={() => {
            setAdjustItem(null);
            qc.invalidateQueries({ queryKey: ['biz-inventory'] });
          }}
        />
      )}
    </div>
  );
}

function AddItemModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}): JSX.Element {
  const [form, setForm] = useState({
    name: '',
    category: 'supplies' as Category,
    unit: 'adet',
    quantityOnHand: 0,
    reorderThreshold: '' as string | number,
    sku: '',
    description: '',
  });
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      bizPost('/biz/inventory', {
        ...form,
        reorderThreshold: form.reorderThreshold !== '' ? Number(form.reorderThreshold) : undefined,
      }),
    onSuccess,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-card w-full max-w-md rounded-2xl shadow-xl p-6">
        <h2 className="text-lg font-bold mb-4">Yeni Stok Kalemi</h2>
        {error && <p className="text-sm text-destructive mb-3">{error}</p>}
        <div className="grid gap-3">
          <div>
            <label className="text-sm font-medium text-foreground">Ad *</label>
            <input
              className="mt-1 w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Örn: Şampuan, Kedi Maması"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-foreground">Kategori *</label>
              <select
                className="mt-1 w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as Category }))}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Birim</label>
              <input
                className="mt-1 w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.unit}
                onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                placeholder="adet, kg, lt..."
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-foreground">Başlangıç Stok</label>
              <input
                type="number"
                min={0}
                className="mt-1 w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.quantityOnHand}
                onChange={(e) =>
                  setForm((f) => ({ ...f, quantityOnHand: parseFloat(e.target.value) || 0 }))
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Min. Eşik</label>
              <input
                type="number"
                min={0}
                className="mt-1 w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.reorderThreshold}
                onChange={(e) =>
                  setForm((f) => ({ ...f, reorderThreshold: e.target.value }))
                }
                placeholder="İsteğe bağlı"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">SKU / Barkod</label>
            <input
              className="mt-1 w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={form.sku}
              onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
              placeholder="İsteğe bağlı"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button
            onClick={onClose}
            className="flex-1 border border-border rounded-lg py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            İptal
          </button>
          <button
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !form.name.trim()}
            className="flex-1 bg-primary text-primary-foreground rounded-lg py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {mut.isPending ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdjustModal({
  item,
  onClose,
  onSuccess,
}: {
  item: InventoryItem;
  onClose: () => void;
  onSuccess: () => void;
}): JSX.Element {
  const [op, setOp] = useState<'add' | 'remove' | 'set'>('add');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      bizPost(`/biz/inventory/${item.id}/adjust`, { op, quantity, notes: notes || undefined }),
    onSuccess,
    onError: (e: Error) => setError(e.message),
  });

  const preview =
    op === 'set'
      ? quantity
      : op === 'add'
        ? item.quantityOnHand + quantity
        : Math.max(0, item.quantityOnHand - quantity);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-card w-full max-w-sm rounded-2xl shadow-xl p-6">
        <h2 className="text-lg font-bold mb-1">Stok Düzenle</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {item.name} — Mevcut: <span className="font-semibold text-foreground">{item.quantityOnHand}</span> {item.unit}
        </p>
        {error && <p className="text-sm text-destructive mb-3">{error}</p>}
        <div className="flex gap-2 mb-4">
          {(['add', 'remove', 'set'] as const).map((o) => (
            <button
              key={o}
              onClick={() => setOp(o)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium border transition-colors ${
                op === o
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted'
              }`}
            >
              {o === 'add' ? <ArrowUp size={14} /> : o === 'remove' ? <ArrowDown size={14} /> : <Equals size={14} />}
              {o === 'add' ? 'Ekle' : o === 'remove' ? 'Çıkar' : 'Ayarla'}
            </button>
          ))}
        </div>
        <div className="mb-3">
          <label className="text-sm font-medium text-foreground">Miktar</label>
          <input
            type="number"
            min={0}
            className="mt-1 w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            value={quantity}
            onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
          />
        </div>
        <div className="mb-4">
          <label className="text-sm font-medium text-foreground">Not (isteğe bağlı)</label>
          <input
            className="mt-1 w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Neden değişti?"
          />
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Sonuç: <span className="font-semibold text-foreground">{preview}</span> {item.unit}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 border border-border rounded-lg py-2 text-sm font-medium hover:bg-muted transition-colors"
          >
            İptal
          </button>
          <button
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            className="flex-1 bg-primary text-primary-foreground rounded-lg py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {mut.isPending ? 'Kaydediliyor...' : 'Uygula'}
          </button>
        </div>
      </div>
    </div>
  );
}
